import { useMemo } from "react";
import { Tooltip } from "react-tooltip";

import {
  AssetModel,
  DropdownItemModel,
  BusinessType,
  AssetType,
} from "@interfaces/index";
import {
  filterAssetsByDate,
  filterAssetsByRange,
  getUploadLaterTooltipContentStr,
} from "@utils/index";
import {
  AssetItem,
  Dropdown,
  PlusSvg,
  AlertTriangleSvg,
} from "@components/index";

export const AssetSelector = ({
  businessType,
  items,
  filteringType = "date",
  height,
  startPeriodDate,
  date,
  startDate,
  endDate,
  value = "",
  hideAddAssetItem,
  hideNotAssetSpecific,
  hideAssetsWithMiles,
  hideUploadLaterInfo,
  excludeTypes,
  onSetAsset,
  error,
  dataTestid,
}: {
  businessType?: BusinessType;
  items: AssetModel[];
  filteringType?: "date" | "range";
  height?: "small" | "medium";
  startPeriodDate?: Date;
  date?: Date;
  startDate?: Date;
  endDate?: Date;
  value?: string;
  hideAddAssetItem?: boolean;
  hideNotAssetSpecific?: boolean;
  hideAssetsWithMiles?: boolean;
  hideUploadLaterInfo?: boolean;
  excludeTypes?: AssetType[];
  onSetAsset?: (assetType: string) => void;
  error?: string;
  dataTestid?: string;
}) => {
  const preparedAssets: DropdownItemModel[] = useMemo(() => {
    let filteredAssets;
    if (filteringType === "date") {
      filteredAssets = filterAssetsByDate({
        assets: items,
        date,
        startPeriodDate,
        excludeTypes,
        hideAssetsWithMiles: businessType === "other" && hideAssetsWithMiles,
      });
    } else if (filteringType === "range") {
      filteredAssets = filterAssetsByRange({
        assets: items,
        startDate,
        endDate,
        excludeTypes,
        hideAssetsWithMiles: businessType === "other" && hideAssetsWithMiles,
      });
    } else {
      filteredAssets = items;
    }

    const sortedAssets = filteredAssets
      .filter(({ disposal }) => !disposal)
      .sort((a, b) => (a.name > b.name ? 1 : -1));
    const sortedDisposedAssets = filteredAssets
      .filter(({ disposal }) => !!disposal)
      .sort((a, b) => (a.name > b.name ? 1 : -1));
    const assets = [...sortedAssets, ...sortedDisposedAssets];

    const formattedAssets = assets.map((asset) => ({
      id: asset.uniqueId,
      title: (
        <div className="flex flex-col gap-2 md:flex-row md:items-center">
          <AssetItem
            type={asset.type}
            name={asset.name}
            isDisposed={!!asset.disposal}
            isDefault={!!asset.isDefault}
          />
          {!hideUploadLaterInfo &&
          (asset.uploadLaterAgreement || asset.uploadLaterInvoice) ? (
            <>
              <div
                className="flex items-center gap-1 text-100 text-black-70 *:size-4"
                data-tooltip-id={`tooltip-upload-later-${asset.uniqueId}`}
                data-tooltip-variant="light"
                data-tooltip-class-name="!rounded-lg shadow-md !p-4 !text-100"
                data-tooltip-html={getUploadLaterTooltipContentStr(
                  asset,
                  "You still can select this asset, but<br />be sure to upload the needed<br />documents before sending the<br />submission",
                  "size_24",
                )}
              >
                <AlertTriangleSvg />
                Documents required
              </div>
              <Tooltip
                id={`tooltip-upload-later-${asset.uniqueId}`}
                className="hidden !opacity-100 md:block"
              />
            </>
          ) : null}
        </div>
      ),
    }));

    if (businessType === "landlord" && !hideNotAssetSpecific) {
      formattedAssets.push({
        id: "not_asset_specific",
        title: <div>Not asset specific</div>,
      });
    }

    if (!hideAddAssetItem) {
      formattedAssets.push({
        id: "new",
        title: (
          <div className="flex items-center gap-x-2 text-primary-300">
            <PlusSvg /> Add Asset
          </div>
        ),
      });
    }

    return formattedAssets;
  }, [
    items,
    filteringType,
    date,
    startDate,
    startPeriodDate,
    endDate,
    businessType,
    excludeTypes,
    hideAddAssetItem,
    hideAssetsWithMiles,
    hideNotAssetSpecific,
    hideUploadLaterInfo,
  ]);

  const handleSelectAsset = (id: string) => {
    if (onSetAsset) {
      onSetAsset(id);
    }
  };

  return (
    <Dropdown
      items={preparedAssets}
      label="Asset"
      noItemsLabel="No assets added yet"
      showClearButton
      value={value}
      error={error}
      height={height}
      onSelect={handleSelectAsset}
      dataTestid={dataTestid}
    />
  );
};
